import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import type { LlmUsageSummary, LlmUsageRecord } from "@/features/admin/hooks/useLlmConfig";

// ═══════════════════════════════════════════════════════════════
// useLlmUsage — fetch LLM usage summary and paginated history
// ═══════════════════════════════════════════════════════════════

const PAGE_SIZE = 20;

export function useLlmUsage() {
  const [summary, setSummary] = useState<LlmUsageSummary | null>(null);
  const [history, setHistory] = useState<LlmUsageRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);

  const loadSummary = useCallback(async () => {
    try {
      const data = await api.get<LlmUsageSummary>("/llm/usage");
      setSummary(data);
    } catch {
      // non-critical — leave summary null
    }
  }, []);

  const loadHistory = useCallback(async (p: number) => {
    try {
      const data = await api.get<LlmUsageRecord[]>(
        `/llm/usage/history?page=${p}&pageSize=${PAGE_SIZE}`,
      );
      setHistory(data);
    } catch {
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([loadSummary(), loadHistory(1)]).finally(() =>
      setIsLoading(false),
    );
  }, [loadSummary, loadHistory]);

  const nextPage = useCallback(() => {
    const next = page + 1;
    setPage(next);
    loadHistory(next);
  }, [page, loadHistory]);

  const prevPage = useCallback(() => {
    const prev = Math.max(1, page - 1);
    setPage(prev);
    loadHistory(prev);
  }, [page, loadHistory]);

  return {
    summary,
    history,
    isLoading,
    page,
    loadSummary,
    loadHistory,
    nextPage,
    prevPage,
  };
}
